import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../features/auth/AuthContext';
import { LEARNING_URL } from '../../utils/learningUrl';
import MentorSupportModal from './MentorSupportModal';
import '../../styles/Navbar.css';
import './MentorSupportModal.css';

const Navbar = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showMentorModal, setShowMentorModal] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Don't show navbar on landing page or auth pages
  if (
    location.pathname === '/' ||
    location.pathname === '/login' ||
    location.pathname === '/register' ||
    location.pathname === '/internship/payment' ||
    location.pathname.startsWith('/admin')
  ) {
    return null;
  }

  const isInternship = user?.module === 'internship';

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <nav className="navbar">
      <div className="navbar-container">
        {/* Brand */}
        <Link
          to={isInternship ? '/internship/dashboard' : '/internship/projects'}
          className="navbar-brand"
        >
          <img src="/final_logo.png" alt="Batos CodeHub" style={{ height: '80px', width: 'auto', objectFit: 'contain' }} />
        </Link>

        {/* Nav Links */}
        {isAuthenticated && (
          <div className={`navbar-links ${menuOpen ? 'open' : ''}`}>
            {isInternship && (
              <>
                <Link
                  to="/internship/dashboard"
                  className={`navbar-link ${isActive('/internship/dashboard') || isActive('/internship/stage') ? 'active' : ''}`}
                  onClick={() => setMenuOpen(false)}
                >
                  <svg className="nav-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                  </svg>
                  My Project
                </Link>
                <Link
                  to="/internship/projects"
                  className={`navbar-link ${isActive('/internship/projects') ? 'active' : ''}`}
                  onClick={() => setMenuOpen(false)}
                >
                  <svg className="nav-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="7" height="7" rx="1" />
                    <rect x="14" y="3" width="7" height="7" rx="1" />
                    <rect x="3" y="14" width="7" height="7" rx="1" />
                    <rect x="14" y="14" width="7" height="7" rx="1" />
                  </svg>
                  Projects
                </Link>
                <a
                  href={LEARNING_URL}
                  className="navbar-link"
                  onClick={() => setMenuOpen(false)}
                >
                  <svg className="nav-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                  </svg>
                  Learning
                </a>
              </>
            )}
          </div>
        )}

        {/* Right Side */}
        <div className="navbar-actions">
          {isAuthenticated ? (
            <>
              {/* Mentor Support Button */}
              <div className="navbar-mentor-wrap">
                <button
                  className="navbar-mentor-btn"
                  onClick={() => setShowMentorModal(true)}
                  title="Mentor Support"
                >
                  <span className="navbar-mentor-dot" />
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 18h2a1 1 0 0 0 1-1v-3a1 1 0 0 0-1-1h-2v5zM6 18H4a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h2v5z"/>
                    <path d="M3 13a9 9 0 0 1 18 0"/>
                    <path d="M18 18a3 3 0 0 1-3 3h-2"/>
                  </svg>
                  <span>Support</span>
                </button>
              </div>

              {/* User badge with integrated logout */}
              <div className="user-info">
                <div className="user-avatar">
                  {user?.full_name?.charAt(0).toUpperCase()}
                </div>
                <div className="user-details">
                  <span className="user-name">{user?.full_name}</span>
                  <span className="user-module-badge" data-module={user?.module}>
                    {user?.module === 'internship' ? '🎓 Internship' : '👤 User'}
                    {user?.user_type === 'corporate' && ' · Corporate'}
                  </span>
                </div>
                <div className="user-info-divider" />
                <button onClick={handleLogout} className="user-logout-btn" title="Logout">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" fill="currentColor"/>
                  </svg>
                </button>
              </div>

              {/* Hamburger (mobile) */}
              <button
                className="navbar-hamburger"
                onClick={() => setMenuOpen(!menuOpen)}
                aria-label="Toggle menu"
              >
                <span /><span /><span />
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="nav-link">Sign In</Link>
              <Link to="/register" className="nav-button">Get Started</Link>
            </>
          )}
        </div>
      </div>

      {showMentorModal && (
        <MentorSupportModal onClose={() => setShowMentorModal(false)} />
      )}
    </nav>
  );
};

export default Navbar;

